/**
 * Shared canvas types used by both frontend and middle-layer.
 *
 * Intentionally has NO dependency on react-grid-layout so it can be imported
 * in Node.js packages. The frontend extends CanvasContainer with LayoutItem
 * locally for react-grid-layout compatibility.
 */

export type CanvasComponentType = "chart" | "table" | "list" | "rich-text";

export interface CanvasComponentConfig {
  sym?: string;
  syms?: string[];
  [key: string]: unknown;
}

export interface CanvasComponent {
  id: number;
  container_id: number;
  label: string;
  contentId: CanvasContentId;
  type: CanvasComponentType;
  position_index: number;
  config: CanvasComponentConfig;
}

/**
 * All react-grid-layout LayoutItem fields are inlined here so this type
 * is self-contained with no frontend dependencies.
 */
/**
 * Tabbed: classic multi-component container with a tab strip.
 * Single: holds exactly one component; tabs are hidden for max chart visibility.
 */
export type CanvasContainerMode = "tabbed" | "single";

export interface CanvasContainer {
  id: number;
  workspace_id: number;
  // LayoutItem fields (react-grid-layout)
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
  isBounded?: boolean;
  /** Defaults to "tabbed" when omitted. Persisted via container.extra_settings.mode. */
  mode?: CanvasContainerMode;
  components: CanvasComponent[];
}

export interface SymbolGroup {
  id: string;
  name: string;
  color: string;
  componentIds: number[];
}

export interface CanvasWorkspace {
  id: number;
  name: string;
  layout: CanvasContainer[];
  groups?: SymbolGroup[];
}

export enum CanvasContentId {
  GAMMA_LEVELS_CHART = "gamma_levels",
  GAMMA_MODEL_CHART = "gamma_model",
  COMBO_STRIKES = "combo_strikes",
  LIVE_PRICES_CHART = "live_prices_chart",
  SG_LEVELS = "sg_levels",
  INDEX_METRICS = "index_metrics",
  SUPP_RESISTANCE_LEVELS = "supp_resistance_levels",
  INDEX_LEVELS = "index_levels",
  AT_SPOTGAMMA = "at_spotgamma",
  MACRO_ECONOMIC_CALENDAR = "macro_economic_calendar",
  EARNINGS = "earnings",
  EARNINGS_CHART = "earnings_chart",
  SCANNER_SQUEEZE_CANDIDATES = "scanner_squeeze_candidates",
  SCANNER_VRP_CANDIDATES = "scanner_vrp_candidates",
  OCC_CHART = "occ_chart",
  ZERO_DTE_EQUITY_VOL_CHART = "zero_dte",
  EQUITY_PUT_CALL_RATIO_CHART = "equity_put_call_ratio_chart",
  EXP_CONCENTRATION_CHART = "exp_concentration",
  FIXED_STRIKE_MATRIX = "fixed_strike_matrix",
  VIX_CHART = "vix",
  TERM_STRUCTURE = "term_structure",
  HIRO_CHART = "hiro_chart",
  VOL_SKEW = "vol_skew",
  EQUITIES_SCANNER = "equities_scanner",
  FOUNDERS_NOTES = "founders_notes",
  STRATEGY_COMPASS = "strategy_compass",
  COMPOSITE_VIEW_CHART = "composite_view",
  SYNTH_COMPOSITE_VIEW_CHART = "synth_composite_view",
  SYNTH_OI_PUT_CALL_IMPACT_CHART = "synth_oi_put_call_impact",
  TOTAL_OI_PUT_CALL_IMPACT_CHART = "total_oi_put_call_impact",
  FLOW_DATA = "flow_data",
  TRACE = "trace",
  OPTIONS_CALCULATOR = "options_calculator",
}
